import * as Haptics from "expo-haptics";

import Animated, {
  AnimatedRef,
  runOnJS,
  runOnUI,
  scrollTo,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
  SharedValue,
} from "react-native-reanimated";
import {
  LongPressGestureHandler,
  Gesture,
  GestureDetector,
} from "react-native-gesture-handler";
import { PanGestureAction, getPanGestureAction } from "./getPanGestureAction";
import { Platform, useWindowDimensions } from "react-native";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WidgetPos, WidgetPosWithSubs } from "../types";

import { AHText, Box } from "arkh";
import { ResizeIndicators } from "./resize/ResizeIndicator";
import { WidgetLayoutProvider } from "widgets";
import { WidgetRemoveButton } from "./WidgetRemoveButton";
import { WidgetSettingsButton } from "./WidgetSettingsButton";
import { clamp } from "./clamp";
import { getHeightBounds } from "./resize/getHeightBounds";
import { getWidgetLeftValue } from "./getWidgetLeftValue";
import { getWidgetSnapPosition } from "./getWidgetSnapPosition";
import { getWidthBounds } from "./resize/getWidthBounds";
import { moveItem } from "../worklets/moveItem";
import { snapToNearest } from "./resize/snapToNearest";
import { useDeleteWigetAlert } from "./useDeleteWidgetAlert";
import { useResizeOptions } from "./useResizeOptions";
import { useThisWidget } from "./useThisWidget";
import { useWidgetMargin } from "./useWidgetMargin";

import { graphql, useFragment } from "react-relay";
import { InteractiveWidgetWrapper_widget$key } from "./__generated__/InteractiveWidgetWrapper_widget.graphql";
import { useIsWidgetSettingsAvailable } from "../WidgetSettings/widgetSettingsAvailable";
import { WidgetSizeT } from "../localTypes/WidgetSizeT";

import { FormattedMessage } from "react-intl";
import { useWidgetLayout } from "./useWidgetLayout";

export type InteractiveWidgetWrapperProps = PropsWithChildren<{
  widget: InteractiveWidgetWrapper_widget$key;
  unitSize: number;
  canEdit: boolean;
  spaceBetweenColumns: number;
  hiddenWidgetId: SharedValue<string | null>;
  widgets: SharedValue<(WidgetPos | WidgetPosWithSubs)[]>;
  scrollRef: AnimatedRef<Animated.ScrollView>;
  isEditing: boolean;
  onRequestEdit: () => void;
  setWidgets: (widgets: (WidgetPos | WidgetPosWithSubs)[]) => void;
  requestDeleteWidget: (widgetId: string) => void;
  openSettings?(_: {
    widgetId: string;
    __typename: string;
    size: WidgetSizeT;
  }): void;
  scrollX: SharedValue<number>;
  numberOfColumns?: number;
  numberOfRows?: number;
  renderContext: "space" | "storecast";
}>;

function InteractiveWidgetWrapperC({
  unitSize,
  widgets,
  hiddenWidgetId,
  onRequestEdit,
  isEditing,
  spaceBetweenColumns,
  requestDeleteWidget,
  setWidgets,
  children,
  scrollRef,
  scrollX,
  widget: widgetRef,
  openSettings,
  canEdit,
  numberOfColumns = 4,
  numberOfRows = 4,
  renderContext,
}: InteractiveWidgetWrapperProps) {
  const margin = useWidgetMargin();

  const widgetData = useFragment(
    graphql`
      fragment InteractiveWidgetWrapper_widget on Widget {
        id
        __typename
        ... on WidgetNote {
          isResizable
        }
        ...widgetSettingsAvailable_widget
      }
    `,
    widgetRef
  );

  const id = widgetData.id;

  const hasSettings = useIsWidgetSettingsAvailable(widgetData);
  const thisWidget = useThisWidget(widgets, widgetData.id);

  const top = useSharedValue(thisWidget.value.y * unitSize);
  const left = useSharedValue(
    getWidgetLeftValue(thisWidget.value, unitSize, spaceBetweenColumns)
  );

  const scrollLeftOffset = useSharedValue<null | number>(null);

  const absLeft = useDerivedValue(
    () =>
      left.value -
      (scrollLeftOffset.value === null
        ? 0
        : scrollLeftOffset.value - scrollX.value)
  );
  const height = useSharedValue(thisWidget.value.h * unitSize);
  const width = useSharedValue(thisWidget.value.w * unitSize);

  const isActiveDnd = useSharedValue(false);
  const isResizing = useSharedValue(false);

  const columnWidth = spaceBetweenColumns + unitSize * 2;
  const dimension = useWindowDimensions();
  const pageWidth = dimension.width;

  const valToPage = useCallback(
    (val: number) => {
      "worklet";
      return clamp(Math.floor(val / columnWidth), 0, 3);
    },
    [columnWidth]
  );
  const isResizable =
    widgetData.isResizable !== false &&
    !(widgetData.__typename === "WidgetSpace");
  const [resizeOptions, possibleSizes] = useResizeOptions({
    thisWidget,
    widgets,
    isResizable,
    numberOfRows,
  });

  useEffect(() => {
    left.value = getWidgetLeftValue(
      thisWidget.value,
      unitSize,
      spaceBetweenColumns
    );
    top.value = thisWidget.value.y * unitSize;
    width.value = thisWidget.value.w * unitSize;
    height.value = thisWidget.value.h * unitSize;
  }, [
    height,
    left,
    spaceBetweenColumns,
    thisWidget.value,
    top,
    unitSize,
    width,
  ]);

  useAnimatedReaction(
    () => thisWidget.value,
    (cur, prev) => {
      if (!cur) {
        return;
      }
      if (prev?.id === "init") {
        left.value = getWidgetLeftValue(cur, unitSize, spaceBetweenColumns);
        top.value = cur.y * unitSize;
        width.value = cur.w * unitSize;
        height.value = cur.h * unitSize;

        return;
      }

      if (!isActiveDnd.value && !isResizing.value) {
        left.value = withTiming(
          getWidgetLeftValue(cur, unitSize, spaceBetweenColumns)
        );
        top.value = withTiming(cur.y * unitSize);
        if (cur.w !== prev?.w) {
          width.value = withTiming(cur.w * unitSize);
        }
        if (cur.h !== prev?.h) {
          height.value = withTiming(cur.h * unitSize);
        }
      }
    },
    [unitSize, spaceBetweenColumns]
  );

  useAnimatedReaction(
    () => isResizing.value,
    (c, p) => {
      if (c === false && p === true) {
        const cur = thisWidget.value;
        left.value = withTiming(
          getWidgetLeftValue(cur, unitSize, spaceBetweenColumns)
        );
        top.value = withTiming(cur.y * unitSize);
        width.value = withTiming(cur.w * unitSize);
        height.value = withTiming(cur.h * unitSize);
      }
    },
    [unitSize, spaceBetweenColumns]
  );

  const scale = useSharedValue(1);
  const timer = useSharedValue(0);

  const timeoutRef = useRef<undefined | ReturnType<typeof setTimeout>>(
    undefined
  );
  const pendingDirection = useSharedValue<"left" | "right" | "none">("none");

  const move = useCallback(
    (direction: "left" | "right") => {
      "worklet";
      scrollTo(
        scrollRef,
        clamp(
          valToPage(scrollX.value + columnWidth / 2) +
            (direction === "left" ? -1 : 1),
          0,
          3
        ) * columnWidth,
        0,
        true
      );
    },
    [columnWidth, scrollRef, scrollX, valToPage]
  );

  const requestPageShift = useCallback(
    (direction: "left" | "right" | "none") => {
      if (pendingDirection.value === direction) {
        return;
      }
      pendingDirection.value = direction;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (direction === "none") {
        return;
      }
      timeoutRef.current = setTimeout(() => {
        pendingDirection.value = "none";
        runOnUI(move)(direction);
      }, 1000);
    },
    [move, pendingDirection]
  );
  const action = useSharedValue<PanGestureAction>("ignore");

  const ctx = useSharedValue<{
    startX: number;
    startY: number;
    width: number;
    height: number;

    widget: WidgetPos;
    pageShiftPending: "left" | "right" | "none";
    hasStarted: boolean;
  }>({
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
    widget: thisWidget.value,
    pageShiftPending: "none",
    hasStarted: false,
  });

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .enabled(isEditing && canEdit)
        .activateAfterLongPress(Platform.OS === "web" ? 0 : 70)
        .maxPointers(1)
        .onStart((event) => {
          action.value = getPanGestureAction({
            x: event.x,
            y: event.y,
            width: width.value,
            height: height.value,
            resizeOptions: resizeOptions.value,
          });

          ctx.value.startX = left.value;
          ctx.value.startY = top.value;
          ctx.value.width = width.value;
          ctx.value.height = height.value;
          ctx.value.widget = thisWidget.value;
          ctx.value.pageShiftPending = "none";
          ctx.value.hasStarted = false;
        })
        .onChange((event) => {
          switch (action.value) {
            case "rearrange": {
              if (!isActiveDnd.value) {
                scrollLeftOffset.value = scrollX.value;
                if (Platform.OS === "ios") {
                  runOnJS(Haptics.impactAsync)(
                    Haptics.ImpactFeedbackStyle.Heavy
                  );
                }
              }
              isActiveDnd.value = true;
              left.value = ctx.value.startX + event.translationX;
              top.value = ctx.value.startY + event.translationY;
              const currentPage = valToPage(scrollX.value + columnWidth / 2);

              const startOfCurrentPage = columnWidth * currentPage;
              const endOfCurrentPage = startOfCurrentPage + pageWidth;
              const extraDiff = 10;

              if (
                absLeft.value + ctx.value.width - extraDiff >
                  endOfCurrentPage &&
                pendingDirection.value !== "right"
              ) {
                runOnJS(requestPageShift)("right");
              } else if (
                absLeft.value + extraDiff < startOfCurrentPage &&
                pendingDirection.value !== "left"
              ) {
                runOnJS(requestPageShift)("left");
              }

              if (
                !(
                  absLeft.value + ctx.value.width > endOfCurrentPage ||
                  absLeft.value < startOfCurrentPage
                ) &&
                pendingDirection.value !== "none"
              ) {
                runOnJS(requestPageShift)("none");
              }

              break;
            }
            case "resize-top": {
              isResizing.value = true;
              const heightBounds = getHeightBounds(
                possibleSizes.value,
                ctx.value.widget.w,
                resizeOptions.value["top"].expand + ctx.value.widget.h
              );
              top.value = clamp(
                ctx.value.startY + event.translationY,
                ctx.value.startY - resizeOptions.value.top.expand * unitSize,
                (ctx.value.widget.y + ctx.value.widget.h - heightBounds.min) *
                  unitSize
              );
              height.value = clamp(
                ctx.value.height - event.translationY,
                heightBounds.min * unitSize,
                heightBounds.max * unitSize
              );
              break;
            }
            case "resize-bottom": {
              isResizing.value = true;
              const heightBounds = getHeightBounds(
                possibleSizes.value,
                ctx.value.widget.w,
                resizeOptions.value["bottom"].expand + ctx.value.widget.h
              );
              height.value = clamp(
                ctx.value.height + event.translationY,
                heightBounds.min * unitSize,
                heightBounds.max * unitSize
              );
              break;
            }
            case "resize-left": {
              isResizing.value = true;
              const widthBounds = getWidthBounds(
                possibleSizes.value,
                ctx.value.widget.h,
                2
              );
              left.value = clamp(
                ctx.value.startX + event.translationX,
                ctx.value.widget.c * columnWidth,
                (ctx.value.widget.c + 0.5) * columnWidth
              );
              width.value = clamp(
                ctx.value.width - event.translationX,
                widthBounds.min * unitSize,
                widthBounds.max * unitSize
              );
              break;
            }
            case "resize-right": {
              isResizing.value = true;
              const widthBounds = getWidthBounds(
                possibleSizes.value,
                ctx.value.widget.h,
                2
              );
              width.value = clamp(
                ctx.value.width + event.translationX,
                widthBounds.min * unitSize,
                widthBounds.max * unitSize
              );
              break;
            }
          }
        })
        .onEnd((event) => {
          ctx.value.hasStarted = false;
          switch (action.value) {
            case "rearrange": {
              runOnJS(requestPageShift)("none");
              const { x, y, c } = getWidgetSnapPosition(
                ctx.value.widget,

                unitSize,
                {
                  x: absLeft.value,
                  y: top.value,
                },
                numberOfColumns,
                numberOfRows
              );
              const target = {
                h: ctx.value.widget.h,
                w: ctx.value.widget.w,
                id,
                kind: ctx.value.widget.kind,
                y,
                x,
                c,
              };

              ctx.value.pageShiftPending = "none";
              const { widgets: newWidgets, success } = moveItem(
                id,
                target,
                widgets.value,
                numberOfColumns,
                numberOfRows
              );

              if (success && Platform.OS === "ios") {
                runOnJS(Haptics.impactAsync)(Haptics.ImpactFeedbackStyle.Heavy);
              }

              widgets.value = newWidgets;
              const newWidget = newWidgets.find((w) => w.id === id);
              if (!newWidget) {
                throw new Error(
                  "InteractiveWidgetWrapper Widget not found after rearrange"
                );
              }
              left.value = absLeft.value;
              scrollLeftOffset.value = null;
              left.value = withTiming(
                getWidgetLeftValue(newWidget, unitSize, spaceBetweenColumns)
              );
              top.value = withTiming(newWidget.y * unitSize);
              timer.value = withTiming(timer.value + 1, undefined, () => {
                isActiveDnd.value = false;

                runOnJS(setWidgets)(newWidgets);
              });

              break;
            }
            case "resize-top": {
              const h = snapToNearest(
                Math.round(height.value / unitSize),
                possibleSizes.value
                  .filter(
                    ({ w, h }) =>
                      w === ctx.value.widget.w &&
                      h <=
                        ctx.value.widget.h + resizeOptions.value["top"].expand
                  )
                  .map(({ h }) => h)
              );
              isResizing.value = false;
              const newPositions = widgets.value.map((_widget) =>
                _widget.id === id
                  ? {
                      ...ctx.value.widget,
                      y: ctx.value.widget.y - (h - ctx.value.widget.h),
                      h,
                    }
                  : _widget
              );
              widgets.value = newPositions;
              timer.value = withTiming(timer.value + 1, undefined, () => {
                runOnJS(setWidgets)(newPositions);
              });
              break;
            }
            case "resize-bottom": {
              const h = snapToNearest(
                Math.round(height.value / unitSize),
                possibleSizes.value
                  .filter(
                    ({ w, h }) =>
                      w === ctx.value.widget.w &&
                      h <=
                        ctx.value.widget.h +
                          resizeOptions.value["bottom"].expand
                  )
                  .map(({ h }) => h)
              );

              isResizing.value = false;
              const newPositions = widgets.value.map((_widget) =>
                _widget.id === id
                  ? {
                      ...ctx.value.widget,
                      h,
                    }
                  : _widget
              );
              widgets.value = newPositions;
              timer.value = withTiming(timer.value + 1, undefined, () => {
                runOnJS(setWidgets)(newPositions);
              });

              break;
            }
            case "resize-left": {
              const w = snapToNearest(
                Math.round(width.value / unitSize),
                possibleSizes.value
                  .filter(({ w, h }) =>
                    h === ctx.value.widget.h &&
                    w <= ctx.value.widget.w + resizeOptions.value["left"].expand
                      ? 1
                      : 0
                  )
                  .map(({ w }) => w)
              );
              isResizing.value = false;
              const newPositions = widgets.value.map((_widget) =>
                _widget.id === id
                  ? {
                      ...ctx.value.widget,
                      x: Math.round(left.value / (columnWidth / 2)) % 2,
                      w,
                    }
                  : _widget
              );
              widgets.value = newPositions;
              timer.value = withTiming(timer.value + 1, undefined, () => {
                runOnJS(setWidgets)(newPositions);
              });
              break;
            }
            case "resize-right": {
              const w = snapToNearest(
                Math.round(width.value / unitSize),
                possibleSizes.value
                  .filter(({ w, h }) =>
                    h === ctx.value.widget.h &&
                    w <=
                      ctx.value.widget.w + resizeOptions.value["right"].expand
                      ? 1
                      : 0
                  )
                  .map(({ w }) => w)
              );
              isResizing.value = false;
              const newPositions = widgets.value.map((_widget) =>
                _widget.id === id ? { ...ctx.value.widget, w } : _widget
              );
              widgets.value = newPositions;
              timer.value = withTiming(timer.value + 1, undefined, () => {
                runOnJS(setWidgets)(newPositions);
              });

              break;
            }
          }
          action.value = "ignore";
        })
        .onFinalize(() => {
          if (ctx.value.hasStarted) {
            action.value = "ignore";
            scrollLeftOffset.value = null;
            isResizing.value = false;
            isActiveDnd.value = false;
            width.value = withTiming(ctx.value.width);
            height.value = withTiming(ctx.value.height);
            left.value = withTiming(ctx.value.startX);
            top.value = withTiming(ctx.value.startY);
          }
          ctx.value.hasStarted = false;
        }),
    [
      isEditing,
      canEdit,
      action,
      width,
      height,
      resizeOptions.value,
      ctx.value,
      left,
      top,
      thisWidget.value,
      isActiveDnd,
      valToPage,
      scrollX.value,
      columnWidth,
      pageWidth,
      absLeft.value,
      pendingDirection.value,
      scrollLeftOffset,
      requestPageShift,
      isResizing,
      possibleSizes.value,
      unitSize,
      numberOfColumns,
      numberOfRows,
      id,
      widgets,
      spaceBetweenColumns,
      timer,
      setWidgets,
    ]
  );

  const _onRemove = useDeleteWigetAlert({
    widgetIsShared: !isResizable,
    renderContext,
    onDelete: (id) => {
      scale.value = withTiming(0, undefined, () => {
        runOnJS(requestDeleteWidget)(id);
      });
    },
  });
  const onPressDelete = useCallback(() => _onRemove(id), [_onRemove, id]);

  const opacity = useDerivedValue(() => (hiddenWidgetId.value === id ? 0 : 1));

  const style = useAnimatedStyle(() => {
    const isMoving = isActiveDnd.value || isResizing.value;
    const zIndex = isActiveDnd.value || isResizing.value ? 1 : 0;
    return {
      position: "absolute",
      padding: margin,
      zIndex,
      top: 0,
      left: 0,
      opacity: opacity.value,
      width: width.value,
      height: height.value,

      transform: [
        {
          translateX: isMoving ? absLeft.value : Math.floor(absLeft.value),
        },
        {
          translateY: isMoving ? top.value : Math.floor(top.value),
        },
        {
          scale: withSpring(isActiveDnd.value === true ? 1.02 : 1, {
            stiffness: 700,
            damping: 20,
          }),
        },
      ],
    };
  });

  const longPressGestureRef = useRef(null);

  const [l, setL] = useState({
    w: 0,
    h: 0,
  });

  const updateLayout = useCallback(() => {
    setL({
      w: thisWidget.value.w,
      h: thisWidget.value.h,
    });
  }, [thisWidget.value.h, thisWidget.value.w]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, thisWidget.value.w, thisWidget.value.h]);

  useAnimatedReaction(
    () => isResizing.value,
    (isR, prev) => {
      if (isR !== prev) {
        runOnJS(updateLayout)();
      }
    }
  );

  const widgetLayout = useWidgetLayout(unitSize, l);
  return (
    <Animated.View style={style} renderToHardwareTextureAndroid={isEditing}>
      <GestureDetector gesture={panGesture}>
        <Animated.View style={{ height: "100%" }}>
          <LongPressGestureHandler
            ref={longPressGestureRef}
            onActivated={onRequestEdit}
            enabled={!isEditing && canEdit && Platform.OS !== "web"}
            maxDist={35}
          >
            <Animated.View style={{ height: "100%" }}>
              <Box
                cornerRadius="medium"
                style={{
                  width: "100%",
                  height: "100%",
                  overflow:
                    widgetData.__typename === "WidgetSpace"
                      ? "visible"
                      : "hidden",
                  ...{ cursor: isEditing ? ("grab" as any) : undefined },
                }}
              >
                <WidgetLayoutProvider value={widgetLayout}>
                  {children}
                </WidgetLayoutProvider>

                {isEditing ? (
                  <>
                    <Box
                      cornerRadius="medium"
                      style={{
                        margin:
                          widgetData.__typename === "WidgetSpace" ? -margin : 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "white",
                        opacity: 0.7,
                        position: "absolute",
                      }}
                    />
                    {isResizable ? (
                      <ResizeIndicators
                        id={id}
                        widgets={widgets}
                        numberOfRows={numberOfRows}
                      />
                    ) : (
                      <Box
                        pointerEvents="none"
                        style={{
                          position: "absolute",
                          left: 0,

                          bottom: 0,
                          right: 0,
                          top: 0,
                          justifyContent: "center",
                          alignContent: "center",
                        }}
                        spaceHorizontal="medium"
                        spaceBottom="small"
                      >
                        {widgetData.__typename !== "WidgetSpace" ? (
                          <AHText textAlign="center" type="subtitle">
                            {/** TODO: widget shared across spaces */}
                            <FormattedMessage
                              description="Shared widget body message"
                              defaultMessage="Widget is not resizeable due to being shared across multiple dashboards."
                            />
                          </AHText>
                        ) : null}
                      </Box>
                    )}
                  </>
                ) : null}
              </Box>
            </Animated.View>
          </LongPressGestureHandler>
        </Animated.View>
      </GestureDetector>
      {isEditing && hasSettings && openSettings ? (
        <WidgetSettingsButton
          padding={widgetData.__typename === "WidgetSpace" ? 0 : margin}
          onPress={() => {
            openSettings({
              widgetId: widgetData.id,
              size: {
                w: thisWidget.value.w,
                h: thisWidget.value.h,
              } as WidgetSizeT,
              __typename: widgetData.__typename,
            });
          }}
        />
      ) : null}
      {isEditing ? (
        <WidgetRemoveButton
          onPress={onPressDelete}
          padding={widgetData.__typename === "WidgetSpace" ? 0 : margin}
        />
      ) : null}
    </Animated.View>
  );
}

export const InteractiveWidgetWrapper = React.memo(InteractiveWidgetWrapperC);
